import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { View } from 'react-native';
import Section from 'design-system/Section';
import { CardView } from 'design-system/CardView';
import { IconArrowBack } from 'design-system/icons';
import { SettingsItemSwitch } from 'design-system/SettingsItem';
import { Amplitude } from 'utils';
import Modal from 'utils/packages/Modal';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import useFetchOld, { muteAll } from 'hooks/useFetch';
import { editSubscription, getSubscriptionsById, } from 'features/subscriptions/actions';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { makeSelectSubscriptionById, selectIncludeIncomeSubscriptions, selectOrderedActiveSubscriptions, } from 'features/subscriptions/selectors';
import useOpenSubscription from 'features/subscriptions/hooks/useOpenSubscription';
import { useRequiredBenefitCallbackWithSpaces } from 'features/premium/hooks/useRequiredBenefitCallback';
import SubscriptionPrediction from 'features/subscriptions/components/transactions/SubscriptionPrediction';
import { styles } from './styles';
import SetRecurringModal from './SetRecurringModal';
const TransactionSubscriptionSection = ({ transaction, skipGetSubscription, }) => {
    const [isRepeating, setIsRepeating] = useState(false);
    const dispatch = useAppDispatch();
    const memoSelectSubscriptionById = useMemo(makeSelectSubscriptionById, []);
    const subscription = useAppSelector((store) => memoSelectSubscriptionById(store, 'subscriptionId' in transaction ? transaction?.subscriptionId : undefined));
    const activeSubscriptions = useAppSelector(selectOrderedActiveSubscriptions);
    const subscriptionId = 'subscriptionId' in transaction ? transaction?.subscriptionId : null;
    useEffect(() => {
        if (subscriptionId && !skipGetSubscription) {
            dispatch(getSubscriptionsById(subscriptionId));
        }
    }, [skipGetSubscription, subscriptionId]);
    const includeIncome = useAppSelector(selectIncludeIncomeSubscriptions);
    const hasSubscriptionSwitch = !((!includeIncome && transaction.amount > 0) ||
        (transaction.category?.id === 'internal' &&
            (!('subscriptionId' in transaction) || !transaction.subscriptionId)));
    const hasLinkedSubscription = Boolean(((subscription?.predictions && subscription.predictions[0]?.date) ||
        subscription?.transactions?.[0]?.bookingDate) &&
        subscription.isActive);
    const prevHasLinkedSubscription = useRef(hasLinkedSubscription);
    useEffect(() => {
        if (!hasLinkedSubscription && prevHasLinkedSubscription.current) {
            setIsRepeating(false);
        }
        prevHasLinkedSubscription.current = hasLinkedSubscription;
    }, [hasLinkedSubscription]);
    const navigation = useMainStackNavigation();
    const openSubscription = useOpenSubscription();
    const onOpenSubscription = useCallback(() => {
        if (!('subscriptionId' in transaction) ||
            transaction.subscriptionId === null) {
            return;
        }
        const { subscriptionId } = transaction;
        openSubscription(subscriptionId);
    }, [openSubscription, transaction]);
    const [, , , fetch] = useFetchOld(muteAll);
    const onCreateSubscription = useRequiredBenefitCallbackWithSpaces(useCallback(() => {
        Modal.hide();
        Amplitude.logEvent('Transaction.SubscriptionAdded');
        if ('subscriptionId' in transaction &&
            transaction.subscriptionId != null) {
            fetch(editSubscription(transaction.subscriptionId, {
                isActive: true,
            }));
        }
        else {
            navigation.navigateDeprecated('RenameSubscription', {
                createTransaction: transaction,
                onSuccess: () => {
                    setIsRepeating(true);
                },
            });
        }
    }, [transaction]), 'recurringPayments');
    const onAddToExistingSubscription = useCallback(() => {
        Modal.hide();
        Amplitude.logEvent('Transaction.SubscriptionAdded');
        navigation.navigateDeprecated('Subscriptions', {
            data: transaction,
        });
    }, [navigation, transaction]);
    const onCloseSubscriptions = useCallback(() => {
        Modal.hide();
        setIsRepeating(false);
    }, []);
    const onSetSubscription = useCallback((value) => {
        if (!value)
            return;
        setIsRepeating(value);
        if (activeSubscriptions.length === 0) {
            Modal.showConfirmation({
                title: 'Recurring Payment',
                subtitle: 'Would you like to mark this as a recurring payment?',
                confirmButtonTitle: 'Yes, let’s do it',
                cancelButtonTitle: 'No, thanks',
                onConfirm: onCreateSubscription,
                onPressCancel: onCloseSubscriptions,
                onBackdropPress: () => setIsRepeating(false),
                onSwipeComplete: () => setIsRepeating(false),
            });
        }
        else {
            SetRecurringModal.show(onCreateSubscription, onAddToExistingSubscription, () => setIsRepeating(false));
        }
    }, [
        activeSubscriptions.length,
        onCreateSubscription,
        onAddToExistingSubscription,
        onCloseSubscriptions,
    ]);
    const content = useMemo(() => {
        if (hasLinkedSubscription && subscription) {
            return (<SubscriptionPrediction onPress={onOpenSubscription} isPredicted={!!subscription.predictions?.[0]?.date} date={subscription.predictions?.[0]?.date ||
                    subscription.transactions?.[0]?.bookingDate} currency={subscription.nativePrice.currency} amount={subscription.isIncome
                    ? subscription.nativePrice.amount
                    : -subscription.nativePrice.amount}/>);
        }
        // This transaction either has no subscription or it is inactive
        return hasSubscriptionSwitch ? (<View style={styles.webPadding}>
        <SettingsItemSwitch icon={<IconArrowBack gradientKey="darkPink"/>} title="Is it recurring?" onValueChange={onSetSubscription} value={subscription?.isActive || isRepeating} disabled={subscription?.isActive || isRepeating}/>
      </View>) : null;
    }, [
        hasLinkedSubscription,
        hasSubscriptionSwitch,
        isRepeating,
        onOpenSubscription,
        onSetSubscription,
        subscription,
    ]);
    const isIncome = subscription
        ? subscription.isIncome === true
        : transaction.amount > 0;
    if (hasSubscriptionSwitch || hasLinkedSubscription) {
        return (<Section title={isIncome ? 'Regular income' : 'Recurring payment'} style={styles.section}>
        <CardView noMarginTop noMarginBottom>
          {content}
        </CardView>
      </Section>);
    }
    return null;
};
export default TransactionSubscriptionSection;
